import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import { BottomNav } from "../components/BottomNav";
import { ImageEditor } from "../components/ImageEditor";
import { listPending, queuePhoto, removePending, clearPendingQueue } from "../lib/offlineQueue";
import { saveReceiptLocally } from "../lib/saveToDevice";
import { takeSharedFiles } from "../lib/shareTarget";
import { compressImageForUpload, friendlyUploadError } from "../lib/uploadImage";
import type { ReceiptUploadResponse } from "../types";

/**
 * Camera capture using getUserMedia (iOS Safari + Android Chrome).
 * After capture: optional crop / rotate, then upload.
 * Also accepts Photos/Files pickers, paste, and Android share-target.
 */
export function CapturePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const txnHint = params.get("txn");

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const photoRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [preview, setPreview] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [originalBlob, setOriginalBlob] = useState<Blob | null>(null);
  const [editing, setEditing] = useState(false);
  const [fromCamera, setFromCamera] = useState(false);
  const [isPdf, setIsPdf] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const uploadingRef = useRef(false);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    stopCamera();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      setCameraError(
        "Camera unavailable. Use Photos or Files below (works on all devices).",
      );
    }
  }, [stopCamera]);

  function setCaptured(b: Blob, source: "camera" | "file") {
    const url = URL.createObjectURL(b);
    setOriginalBlob(b);
    setBlob(b);
    setPreview(url);
    setFromCamera(source === "camera");
    setIsPdf(false);
    setEditing(true);
    stopCamera();
  }

  function onFile(file: File | null) {
    if (!file) return;
    const pdf =
      file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (pdf) {
      setBlob(file);
      setOriginalBlob(null);
      setPreview(null);
      setFromCamera(false);
      setIsPdf(true);
      setEditing(false);
      stopCamera();
      return;
    }
    setCaptured(file, "file");
  }

  useEffect(() => {
    if (!editing && !preview && !blob) {
      void startCamera();
    }
    listPending().then((p) => setPendingCount(p.length));
    return () => stopCamera();
  }, [startCamera, stopCamera, editing, preview, blob]);

  useEffect(() => {
    if (params.get("share_error")) {
      setMessage("Could not receive the shared file. Try Photos / Files below.");
    } else if (params.get("share_empty")) {
      setMessage("Nothing was shared. Pick a photo or PDF instead.");
    }

    if (!params.get("shared")) return;
    (async () => {
      try {
        const files = await takeSharedFiles();
        if (files[0]) {
          onFile(files[0]);
          setMessage("Shared file ready — review and upload.");
        } else {
          setMessage("No shared file found. Try again from the share sheet.");
        }
      } catch {
        setMessage("Could not open the shared file.");
      }
    })();
    // intentionally once on shared=1
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    async function onPaste(e: ClipboardEvent) {
      if (busy || editing) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          const f = item.getAsFile();
          if (f) onFile(f);
          return;
        }
        if (item.type === "application/pdf") {
          e.preventDefault();
          const f = item.getAsFile();
          if (f) onFile(f);
          return;
        }
      }
    }
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [busy, editing]);

  function captureFrame() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (b) => {
        if (!b) return;
        setCaptured(b, "camera");
      },
      "image/jpeg",
      0.92,
    );
  }

  function retake() {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setBlob(null);
    setOriginalBlob(null);
    setFromCamera(false);
    setIsPdf(false);
    setEditing(false);
    setMessage(null);
    void startCamera();
  }

  function onEditApply(nextBlob: Blob, nextUrl: string) {
    if (preview) URL.revokeObjectURL(preview);
    setBlob(nextBlob);
    setPreview(nextUrl);
    setEditing(false);
  }

  async function appendOriginal(
    form: FormData,
    source: Blob,
  ): Promise<void> {
    let originalUpload = source;
    if (source.type.startsWith("image/")) {
      originalUpload = await compressImageForUpload(source, {
        maxSide: 3200,
        quality: 0.92,
      });
    }
    const mime = originalUpload.type || "image/jpeg";
    form.append(
      "original",
      new File([originalUpload], "receipt-original.jpg", { type: mime }),
    );
  }

  async function upload() {
    if (!blob || uploadingRef.current) return;
    uploadingRef.current = true;
    setBusy(true);
    setMessage(null);

    const sourceOriginal =
      !isPdf && originalBlob && originalBlob !== blob ? originalBlob : null;

    if (!navigator.onLine) {
      await queuePhoto(
        blob,
        isPdf ? "receipt.pdf" : "receipt.jpg",
        blob.type || (isPdf ? "application/pdf" : "image/jpeg"),
        txnHint ? Number(txnHint) : undefined,
        sourceOriginal ?? undefined,
      );
      const pending = await listPending();
      setPendingCount(pending.length);
      setMessage("You are offline — photo queued and will upload when you are back online.");
      uploadingRef.current = false;
      setBusy(false);
      return;
    }

    try {
      let uploadBlob = blob;
      if (!isPdf && blob.type.startsWith("image/")) {
        uploadBlob = await compressImageForUpload(blob);
      }
      const filename = isPdf || uploadBlob.type === "application/pdf" ? "receipt.pdf" : "receipt.jpg";
      const mime =
        uploadBlob.type ||
        (filename.endsWith(".pdf") ? "application/pdf" : "image/jpeg");
      const file = new File([uploadBlob], filename, { type: mime });

      const form = new FormData();
      form.append("file", file);
      if (sourceOriginal) {
        await appendOriginal(form, sourceOriginal);
      }
      const result = await api.request<ReceiptUploadResponse>("/receipts/upload", {
        method: "POST",
        body: form,
        headers: {},
      });
      // localStorage survives iOS PWA reloads better than sessionStorage when the
      // browser also triggers a file download after capture.
      const payload = JSON.stringify({ ...result, txnHint });
      try {
        localStorage.setItem("lastUpload", payload);
      } catch {
        /* ignore quota */
      }
      sessionStorage.setItem("lastUpload", payload);

      // Navigate to confirm FIRST. Auto-downloading the image on iOS often
      // interrupts SPA navigation so the match screen never appears.
      navigate("/match");

      if (fromCamera && uploadBlob.type.startsWith("image/")) {
        window.setTimeout(() => {
          try {
            saveReceiptLocally(uploadBlob);
          } catch {
            /* ignore — upload already succeeded */
          }
        }, 800);
      }    } catch (e) {
      const msg = friendlyUploadError(e);
      const shouldQueue =
        !navigator.onLine ||
        /interrupted|failed to fetch|load failed|network/i.test(msg);
      if (shouldQueue) {
        await queuePhoto(
          blob,
          isPdf ? "receipt.pdf" : "receipt.jpg",
          blob.type || (isPdf ? "application/pdf" : "image/jpeg"),
          txnHint ? Number(txnHint) : undefined,
          sourceOriginal ?? undefined,
        );
        setPendingCount((await listPending()).length);
        setMessage(`${msg} Photo kept in retry queue.`);
      } else {
        setMessage(msg);
      }
    } finally {
      uploadingRef.current = false;
      setBusy(false);
    }
  }

  async function flushQueue() {
    const pending = await listPending();
    for (const item of pending) {
      try {
        const form = new FormData();
        form.append("file", item.blob, item.filename);
        if (item.originalBlob) {
          form.append(
            "original",
            item.originalBlob,
            "receipt-original.jpg",
          );
        }
        await api.request("/receipts/upload", {
          method: "POST",
          body: form,
          headers: {},
        });
        await removePending(item.id);
      } catch {
        break;
      }
    }
    setPendingCount((await listPending()).length);
  }

  useEffect(() => {
    const onOnline = () => {
      void flushQueue();
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div>
          <h2 style={{ fontSize: "1.35rem" }}>
            {editing ? "Crop & rotate" : "Add receipt"}
          </h2>
          <p className="muted" style={{ margin: 0 }}>
            {editing
              ? "Adjust the frame, then apply"
              : txnHint
                ? `Linking to transaction #${txnHint}`
                : isPdf
                  ? "PDF ready — tap Use file to match"
                  : "Photo, PDF, paste, or share from your phone"}
          </p>
        </div>
      </header>

      {editing && preview && blob ? (
        <ImageEditor
          sourceUrl={preview}
          sourceBlob={blob}
          onCancel={() => setEditing(false)}
          onApply={onEditApply}
        />
      ) : (
        <>
          <div className="camera-stage">
            {preview ? (
              <img src={preview} alt="Receipt preview" />
            ) : isPdf && blob ? (
              <div
                className="stack"
                style={{
                  height: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "1.5rem",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "2.5rem", opacity: 0.7 }}>PDF</div>
                <strong>PDF selected</strong>
                <p className="muted" style={{ margin: 0 }}>
                  Tap Use file to extract and match — works with FreeNow email receipts.
                </p>
              </div>
            ) : (
              <video ref={videoRef} playsInline muted autoPlay />
            )}
          </div>
          <canvas ref={canvasRef} hidden />

          {cameraError && (
            <p className="muted" style={{ marginTop: "0.75rem" }}>
              {cameraError}
            </p>
          )}
          {message && (
            <div className="alert alert-ok" style={{ marginTop: "0.75rem" }}>
              {message}
            </div>
          )}

          <div className="camera-actions">
            {!preview && !blob ? (
              <>
                <button
                  type="button"
                  className="fab-capture"
                  aria-label="Take photo"
                  onClick={captureFrame}
                  disabled={!!cameraError}
                />
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => photoRef.current?.click()}
                >
                  Photos
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => fileRef.current?.click()}
                >
                  Files
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={retake}
                  disabled={busy}
                >
                  Retake
                </button>
                {preview && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setEditing(true)}
                    disabled={busy}
                  >
                    Crop / rotate
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={upload}
                  disabled={busy || !blob}
                  style={{ flex: 1 }}
                >
                  {busy ? "Uploading…" : isPdf ? "Use file" : "Use photo"}
                </button>
              </>
            )}
          </div>

          {!preview && !blob && (
            <p className="muted" style={{ marginTop: "0.75rem", fontSize: "0.85rem" }}>
              Tip: copy a photo and paste here, or{" "}
              <Link to="/share-setup">set up iPhone Share</Link> for Mail / Photos.
            </p>
          )}
        </>
      )}

      <input
        ref={photoRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          onFile(e.target.files?.[0] || null);
          e.target.value = "";
        }}
      />
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf,.pdf"
        hidden
        onChange={(e) => {
          onFile(e.target.files?.[0] || null);
          e.target.value = "";
        }}
      />

      {pendingCount > 0 && !editing && (
        <div className="stack" style={{ marginTop: "1rem" }}>
          <button
            type="button"
            className="btn btn-ghost btn-block"
            onClick={flushQueue}
          >
            Sync {pendingCount} queued photo{pendingCount > 1 ? "s" : ""}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-block"
            onClick={async () => {
              if (!confirm(`Delete ${pendingCount} queued photo(s) from this phone?`)) return;
              await clearPendingQueue();
              setPendingCount(0);
              setMessage("Queued photos cleared.");
            }}
          >
            Clear queued photos
          </button>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
